<script setup lang="ts">
import { computed } from "vue"
import type Plotly from "plotly.js-dist-min"
import PlotlyChart from "@modules/data/components/PlotlyChart.vue"
import { mergeAlarmThresholdPlotlyLayout } from "@modules/energy-manager/constants/alarmThresholdPlotlyTemplateLayout"
import {
    ALARM_THRESHOLD_HOURLY_DAY_START_INDICES,
    ALARM_THRESHOLD_WEEKDAY_LABELS_FR,
    computeAlarmThresholdYAxisUpperBound,
    enrichAlarmThresholdChartData,
    inferAlarmThresholdFrequency,
} from "@modules/energy-manager/utils/alarmThresholdChartPresentation"

const props = withDefaults(
    defineProps<{
        chartData: Plotly.Data[]
        /** Unité affichée sur l’axe Y (issue de `ConfigVariable.unit` dans le store config). */
        yAxisUnit?: string
        chartOptions?: Partial<Plotly.Layout>
        disableZoom?: boolean
        /**
         * `auto` : déduit de la longueur de série (7 = quotidien, 168 = horaire).
         * Sinon force le format axe / infobulles.
         */
        frequency?: "hourly" | "daily" | "auto"
    }>(),
    {
        frequency: "auto",
    }
)

const resolvedFrequency = computed((): "hourly" | "daily" | undefined => {
    if (props.frequency !== "auto") {
        return props.frequency
    }
    const first = props.chartData[0] as { x?: unknown } | undefined
    const x = first?.x
    const len = Array.isArray(x) ? x.length : 0
    return inferAlarmThresholdFrequency(len)
})

const presentationChartData = computed(() =>
    enrichAlarmThresholdChartData(
        props.chartData,
        resolvedFrequency.value,
        props.yAxisUnit
    )
)

const xAxisForFrequency = computed((): Partial<Plotly.Layout["xaxis"]> => {
    const freq = resolvedFrequency.value
    if (freq === "daily") {
        return {
            type: "category",
            categoryorder: "array",
            categoryarray: [...ALARM_THRESHOLD_WEEKDAY_LABELS_FR],
        }
    }
    if (freq === "hourly") {
        return {
            type: "linear",
            tickmode: "array",
            tickvals: [...ALARM_THRESHOLD_HOURLY_DAY_START_INDICES],
            ticktext: [...ALARM_THRESHOLD_WEEKDAY_LABELS_FR],
        }
    }
    return {}
})

/**
 * Layout Plotly (template seuils + options parent + unité Y + zoom + axe semaine).
 * Avec zoom activé : `yaxis.fixedrange` verrouille l’axe Y pour que la sélection ne zoome
 * que sur l’axe X (comme le déplacement des poignées du rangeslider), pas en vertical.
 */
/** Axe Y : max données + 20 % pour éviter une échelle 0–20 avec des séries &lt; 1 kWh. */
const getYAxisUpperBound = computed(() =>
    computeAlarmThresholdYAxisUpperBound(props.chartData)
)

const mergedLayout = computed(() => {
    const unit = props.yAxisUnit?.trim()
    const yAxisFixedRangeForXOnlyZoom =
        !props.disableZoom &&
        props.chartOptions?.yaxis?.fixedrange === undefined
    const yUpper = getYAxisUpperBound.value

    return mergeAlarmThresholdPlotlyLayout({
        ...props.chartOptions,
        dragmode: props.disableZoom
            ? false
            : (props.chartOptions?.dragmode ?? "zoom"),
        xaxis: {
            ...props.chartOptions?.xaxis,
            ...xAxisForFrequency.value,
        },
        yaxis: {
            ...props.chartOptions?.yaxis,
            ...(unit
                ? {
                      title: {
                          ...props.chartOptions?.yaxis?.title,
                          text: unit,
                      },
                  }
                : {}),
            ...(yAxisFixedRangeForXOnlyZoom ? { fixedrange: true } : {}),
            autorange: false,
            range: [0, yUpper],
        },
    })
})

/** Objet attendu par `PlotlyChart` (`config` gérée en interne par le composant data). */
const plotlyGraph = computed(() => {
    if (!presentationChartData.value?.length) {
        return null
    }
    return {
        data: presentationChartData.value as object[],
        layout: mergedLayout.value as Record<string, unknown>,
    }
})
</script>

<template>
    <!-- min-w-0 : en flex-col, sans cela le contenu Plotly impose une largeur min et déborde -->
    <div
        class="alarm-plotly-chart h-80 w-full min-w-0 max-w-full overflow-hidden p-0"
    >
        <PlotlyChart
            v-if="plotlyGraph"
            class="h-full min-w-0 w-full max-w-full"
            :graph="plotlyGraph"
        />
    </div>
</template>

<style scoped>
/* Aligné sur template_graph.json (light.layout.modebar) — complète la barre d’outils Plotly du composant data */
.alarm-plotly-chart :deep(.modebar) {
    background: #ffffff !important;
}

.alarm-plotly-chart :deep(.modebar-btn) {
    color: rgba(68, 68, 68, 0.3);
}

.alarm-plotly-chart :deep(.modebar-btn.active) {
    color: rgba(68, 68, 68, 0.7);
}
</style>
